import { useEffect, useRef, useState } from 'react';

export type PaymentBanner = { message: string; status: 'processing' | 'confirmed' | 'timeout' } | null;

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 10;

/**
 * Public portal pages (invoice / estimate): after the customer pays, the payment is recorded by the
 * provider webhook a moment later. Polls the page's data endpoint until amount_paid rises.
 *
 * @param dataUrl       The public GET endpoint of the page (returns snake_case JSON with amount_paid).
 * @param documentLabel "invoice" / "estimate" — used in the banner text.
 * @param onConfirmed   Receives the fresh raw response once the payment shows up.
 */
export function usePaymentConfirmationPolling(
    dataUrl: string,
    documentLabel: string,
    onConfirmed: (raw: any) => void,
) {
    const [banner, setBanner] = useState<PaymentBanner>(null);
    const amountPaidBefore = useRef(0);
    const onConfirmedRef = useRef(onConfirmed);
    onConfirmedRef.current = onConfirmed;

    useEffect(() => {
        if (banner?.status !== 'processing') return;

        let attempts = 0;
        const intervalId = setInterval(async () => {
            attempts++;
            try {
                const res = await fetch(dataUrl);
                if (res.ok) {
                    const raw = await res.json();
                    if ((raw.amount_paid ?? 0) > amountPaidBefore.current) {
                        clearInterval(intervalId);
                        onConfirmedRef.current(raw);
                        setBanner({ message: `Payment confirmed and ${documentLabel} updated.`, status: 'confirmed' });
                        return;
                    }
                }
            } catch {
                // Silently ignore polling errors
            }

            if (attempts >= MAX_ATTEMPTS) {
                clearInterval(intervalId);
                setBanner({
                    message: `Payment received. The ${documentLabel} PDF may take a moment to update — refresh the page to see the latest version.`,
                    status: 'timeout',
                });
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [banner?.status, dataUrl, documentLabel]);

    /** Call right after the client-side payment succeeds, with the amount paid before it. */
    const startPolling = (paidBefore: number) => {
        amountPaidBefore.current = paidBefore;
        setBanner({ message: `Payment received! Updating ${documentLabel}...`, status: 'processing' });
    };

    return { banner, startPolling };
}
